'use client';

import { useState } from 'react';
import { cn } from '@/app/utils/cn';
import { ChevronDown, Star } from 'lucide-react';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

// A header cell containing only a plain `#anchorId` paragraph (no marks, no other content)
// is treated as a page anchor — its text sets the wrapper's id and the cell renders empty.
function getAnchorId(cell) {
    const content = cell.content ?? [];
    if (content.length !== 1) return null;
    const para = content[0];
    if (para.type !== 'paragraph') return null;
    if (!para.content || para.content.length !== 1) return null;
    const textNode = para.content[0];
    if (textNode.type !== 'text') return null;
    if (textNode.marks?.length) return null;
    const text = textNode.text ?? '';
    if (!text.startsWith('#')) return null;
    return text.slice(1).trim();
}

// Finds the physical cell in a row that covers a given column index (accounting for colspan)
function getCellAtColumn(row, targetColIndex) {
    let col = 0;
    for (const cell of row) {
        const span = cell.attrs?.colspan ?? 1;
        if (col + span > targetColIndex) return cell;
        col += span;
    }
    return null;
}

function extractCellText(node) {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    if (node.content) return node.content.map(extractCellText).join('');
    return '';
}

function parsePricingTable(content) {
    const tableNode = content?.find((n) => n.type === 'table');
    if (!tableNode) return { headers: [], sections: [], tableId: null };

    const [headerRow, ...dataRows] = tableNode.content;
    let tableId = null;
    const headers = headerRow.content.map((cell) => {
        const anchorId = getAnchorId(cell);
        if (anchorId) tableId = anchorId;
        return extractCellText(cell);
    });

    const sections = [];
    let current = null;
    for (const row of dataRows) {
        const cells = row.content;
        // A single cell spanning all columns signals a new section heading
        if (cells.length === 1 && (cells[0].attrs?.colspan ?? 1) >= headers.length) {
            current = { title: extractCellText(cells[0]), rows: [] };
            sections.push(current);
        } else if (current) {
            current.rows.push(cells);
        }
    }

    return { headers, sections, tableId };
}

export default function PricingTable({ pricingtable }) {
    const { headers, sections, tableId } = parsePricingTable(pricingtable?.content);

    // Plan columns used by the mobile tab selector (excludes anchor and empty header cells)
    const planColumns = headers
        .map((h, i) => ({
            index: i,
            label: h.replace(/\[RECOMENDED\]/i, '').trim(),
            isRecommended: /\[RECOMENDED\]/i.test(h)
        }))
        .filter(({ label }) => label && !label.startsWith('#'));

    const defaultPlanIndex =
        planColumns.find((p) => p.isRecommended)?.index ?? planColumns[0]?.index ?? 1;

    const [openSections, setOpenSections] = useState(() =>
        Object.fromEntries(sections.map((_, i) => [i, true]))
    );
    const [selectedPlanIndex, setSelectedPlanIndex] = useState(defaultPlanIndex);

    if (!headers.length) return null;

    const colCount = headers.length;
    const gridStyle = { gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` };
    const toggle = (index) => setOpenSections((prev) => ({ ...prev, [index]: !prev[index] }));

    return (
        <div id={tableId ?? undefined} className="rounded-2xl border border-blue-625/10">
            {/* Mobile: plan tab selector replaces the sticky header */}
            <div className="flex rounded-t-2xl bg-blue md:hidden">
                {planColumns.map(({ index, label, isRecommended }) => (
                    <button
                        key={index}
                        onClick={() => setSelectedPlanIndex(index)}
                        className={cn(
                            'relative flex-1 px-3 py-4 text-xs font-bold text-white transition-colors',
                            selectedPlanIndex === index ? 'bg-white/20' : 'hover:bg-white/10'
                        )}>
                        {isRecommended && (
                            <span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-fuschia-700 px-2 py-0.5 text-xs font-semibold text-white shadow">
                                <Star className="size-3 fill-white" />
                                Recommended
                            </span>
                        )}
                        {label.toUpperCase()}
                    </button>
                ))}
            </div>

            {/* Desktop: sticky column header */}
            <div
                className="sticky top-0 z-10 hidden rounded-t-2xl bg-blue md:grid"
                style={gridStyle}>
                {headers.map((header, i) => {
                    // Anchor cell — keep the grid column but render nothing
                    if (header.startsWith('#')) {
                        return <div key={i} className="border-r border-white/20 last:border-r-0" />;
                    }
                    const isRecommended = /\[RECOMENDED\]/i.test(header);
                    const label = header.replace(/\[RECOMENDED\]/i, '').trim();
                    return (
                        <div
                            key={i}
                            className="relative border-r border-white/20 px-6 py-7 text-base font-bold text-white last:border-r-0">
                            {isRecommended && (
                                <span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-fuschia-700 px-3 py-1 text-xs font-semibold text-white shadow">
                                    <Star className="size-3 fill-white" />
                                    Recommended
                                </span>
                            )}
                            {label.toUpperCase()}
                        </div>
                    );
                })}
            </div>

            {sections.map((section, sectionIndex) => {
                const isOpen = openSections[sectionIndex] !== false;
                return (
                    <div
                        key={sectionIndex}
                        className="overflow-hidden border-t border-gray-200 last:rounded-b-2xl">
                        <button
                            className="flex w-full items-center gap-3 bg-gray-50 px-4 py-3 text-left text-xl font-normal text-blue-700 hover:bg-gray-100"
                            onClick={() => toggle(sectionIndex)}>
                            {section.title}
                            <span className="ml-auto flex items-center justify-center text-base font-medium text-gray-400">
                                {section.rows.length} feature
                                {section.rows.length !== 1 ? 's' : ''}
                                <ChevronDown
                                    className={cn(
                                        'size-8 shrink-0 transition-transform duration-300 ml-5',
                                        isOpen && 'rotate-180'
                                    )}
                                />
                            </span>
                        </button>

                        <div
                            className={cn(
                                'grid transition-all duration-300',
                                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                            )}>
                            <div className="overflow-hidden">
                                {section.rows.map((row, rowIndex) => (
                                    <div
                                        key={rowIndex}
                                        className={cn(
                                            'border-b border-gray-100 last:border-0',
                                            rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                        )}>
                                        {/* Desktop: all columns */}
                                        <div className="hidden md:grid" style={gridStyle}>
                                            {row.map((cell, cellIndex) => {
                                                const span = cell.attrs?.colspan ?? 1;
                                                return (
                                                    <div
                                                        key={cellIndex}
                                                        style={
                                                            span > 1
                                                                ? { gridColumn: `span ${span}` }
                                                                : undefined
                                                        }
                                                        className="border-r border-gray-100 px-6 py-4 text-base text-gray-600 last:border-r-0">
                                                        <DotBlockEditor
                                                            blocks={{
                                                                type: 'doc',
                                                                content: cell.content ?? []
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Mobile: feature col (0) + selected plan col */}
                                        <div className="grid grid-cols-2 md:hidden">
                                            {[0, selectedPlanIndex].map((colIdx) => {
                                                const cell = getCellAtColumn(row, colIdx);
                                                const span = cell?.attrs?.colspan ?? 1;
                                                const fullWidth = span >= colCount;
                                                return (
                                                    <div
                                                        key={colIdx}
                                                        style={
                                                            fullWidth
                                                                ? { gridColumn: 'span 2' }
                                                                : undefined
                                                        }
                                                        className="border-r border-gray-100 px-4 py-3 text-sm text-gray-600 last:border-r-0">
                                                        {cell && (
                                                            <DotBlockEditor
                                                                blocks={{
                                                                    type: 'doc',
                                                                    content: cell.content ?? []
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
